import { Action, combineReducers } from 'redux';
import { call, put, takeLeading, select, takeLatest, take } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExternalUserDetail, SendOtpViaSmsResponse, VerifyOtpResponse } from 'generated/bffUser/models';
import { SharedObjectsData } from 'generated/sharedObjects/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { ExternalUser } from 'models/context/ExternalUser';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectIsUserInternal } from 'saga/auth/ducks';
import { AllInvoicesActionTypes } from 'saga/billingManagement/allInvoices/ducks';
import { getExternalUserDetailActions, selectContextExternalUser } from 'saga/context/external/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';
import { GetSessionAction } from 'saga/sharedObjects/ducks';

export type RunOTPScreeningType = (payload?: () => void) => void;

/* STATE */
export interface OtpState {
  phoneNoOtp: string;
  attempts: VerifyOtpResponse | null;
  loadingStatus: LoadingStatus;
  showOTPModal: boolean;
  showOTPNoPhoneNumberModal: boolean;
}

/* ACTION TYPES */
export enum OTPActionTypes {
  OTPSendSms = '@@OTP/OTP_SEND_SMS',
  VerifyOTPCode = '@@OTP/VERIFY_OTP_CODE',
  RunOTPScreening = '@@OTP/SCREENING',
  SetLoading = '@@OTP/SET_LOADING',
  SetOTPModal = '@@OTP/SET_MODAL',
  SetOTPNoPhoneNumberModal = '@@OTP/SET_NO_PHONE_NUMBER_MODAL',
}

/* ACTIONS */
export const OTPSendSmsAction = createApiActionCreators(OTPActionTypes.OTPSendSms);
export const verifyOTPCodeAction = createApiActionCreators(OTPActionTypes.VerifyOTPCode);
export const runOTPScreeningAction = createActionCreator(OTPActionTypes.RunOTPScreening);
export const setLoadingAction = createActionCreator(OTPActionTypes.SetLoading);
export const setOTPModalAction = createActionCreator(OTPActionTypes.SetOTPModal);
export const setOTPNoPhoneNumberModalAction = createActionCreator(OTPActionTypes.SetOTPNoPhoneNumberModal);

/* REDUCERS */
const initialState: OtpState = {
  phoneNoOtp: '',
  attempts: null,
  loadingStatus: LoadingStatus.loading,
  showOTPModal: false,
  showOTPNoPhoneNumberModal: false,
};

const phoneNoOtp = createReducer(initialState.phoneNoOtp, {
  [OTPActionTypes.OTPSendSms]: {
    [RequestActionTypes.SUCCESS]: (_state: string, payload: SendOtpViaSmsResponse) => payload.phoneNoOtp,
    [RequestActionTypes.FAILURE]: () => null,
  },
});
const attempts = createReducer(initialState.attempts, {
  [OTPActionTypes.VerifyOTPCode]: {
    [RequestActionTypes.SUCCESS]: () => null,
    [RequestActionTypes.FAILURE]: (_state: VerifyOtpResponse, payload: VerifyOtpResponse) => payload,
  },
});
const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [OTPActionTypes.VerifyOTPCode]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [OTPActionTypes.VerifyOTPCode]: (state: LoadingStatus, status: LoadingStatus) => status,
    [AllInvoicesActionTypes.PostEbillFileDownloadInitOtp]: {
      [RequestActionTypes.SUCCESS]: () => LoadingStatus.loading,
    },
  }
);
const showOTPModal = createReducer(initialState.showOTPModal, {
  [OTPActionTypes.SetOTPModal]: (state: boolean, payload: boolean) => payload,
});
const showOTPNoPhoneNumberModal = createReducer(initialState.showOTPNoPhoneNumberModal, {
  [OTPActionTypes.SetOTPNoPhoneNumberModal]: (state: boolean, payload: boolean) => payload,
});

export default combineReducers<OtpState>({
  phoneNoOtp,
  attempts,
  loadingStatus,
  showOTPModal,
  showOTPNoPhoneNumberModal,
});

/* SELECTORS */
export const selectOtp = (state: AppState) => state.otp;
export const selectPhoneNoOtp = (state: AppState): string => selectOtp(state).phoneNoOtp;
export const selectAttempts = (state: AppState): VerifyOtpResponse | null => selectOtp(state).attempts;
export const selectOtpLoading = (state: AppState): LoadingStatus => selectOtp(state).loadingStatus;
export const selectShowOTPModal = (state: AppState): boolean => selectOtp(state).showOTPModal;
export const selectShowOTPNoPhoneNumberModal = (state: AppState): boolean => selectOtp(state).showOTPNoPhoneNumberModal;

/* SAGAS */
function* ValidateOtp() {
  yield put(GetSessionAction.request());

  const sharedObjectsSuccess: Action & { payload: SharedObjectsData } = yield take(GetSessionAction.success().type);
  const sharedObjects = sharedObjectsSuccess?.payload;
  const actualTime = Math.floor(Date.now() / 1000);
  const otpValidTime = Number(selectProperty('gui.otp.validationTimeReserveSeconds'));
  const otpCheckValidFrom = sharedObjects?.generalLoginData?.otpCheckValidFrom;
  const lastOtpCheckTime = otpCheckValidFrom && Math.floor(new Date(otpCheckValidFrom).getTime() / 1000);

  const validOtp = lastOtpCheckTime && lastOtpCheckTime >= actualTime - otpValidTime;

  return !!validOtp;
}

function* ValidatePhone() {
  let externalUser: ExternalUserDetail = yield select(selectContextExternalUser);
  const dummyPhoneNumber = selectProperty('gui.otp.phoneNoOTP.dummy.default');

  if (externalUser) {
    yield put(
      getExternalUserDetailActions.request({
        cpRefNo: externalUser?.cpRefNo ?? '',
        forceCheck: true,
      })
    );

    const newExternalUserSuccess: Action & { payload: ExternalUser } = yield take(
      getExternalUserDetailActions.success().type
    );

    externalUser = newExternalUserSuccess?.payload;
  }

  const validPhone = externalUser?.phoneNoOTP && externalUser?.phoneNoOTP !== dummyPhoneNumber;

  return !!validPhone;
}

function* OTPSendSms() {
  const request: ApiClientRequest = {
    apiCall: call(api.OTPSendSms, yield getApiUrl(environment.urlConfig.user)),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const sendOtpViaSmsResponseData: SendOtpViaSmsResponse = resp.data;

    if (sendOtpViaSmsResponseData.phoneNoOtp) {
      yield put(OTPSendSmsAction.success(sendOtpViaSmsResponseData));
      yield put(getKahlActions.request({ infoMessageIds: 'ID0766' }));
    } else {
      yield put(OTPSendSmsAction.success({ phoneNoOtp: '' }));
    }
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'UM-ID04' }));
    yield put(OTPSendSmsAction.failure());
  }
}

function* RunOTPScreening(action: { type: typeof OTPActionTypes.RunOTPScreening; payload: () => void }) {
  const screeningDone = action?.payload;
  const validPhone: boolean = yield call(ValidatePhone);
  const validOtp: boolean = yield call(ValidateOtp);
  const isUserInternal: boolean = yield select(selectIsUserInternal);

  if (((validPhone && validOtp) || isUserInternal) && screeningDone) {
    yield put(setOTPModalAction(false));
    screeningDone();

    return;
  }

  if (!validPhone) {
    yield put(setOTPNoPhoneNumberModalAction(true));

    return;
  } else if (!validOtp) {
    yield put(setOTPModalAction(true));
  }

  if (!(validPhone && validOtp) && screeningDone) {
    while (true) {
      yield take(verifyOTPCodeAction.success().type);

      const frozenSagaValidPhone: boolean = yield call(ValidatePhone);
      const frozenSagaValidOtp: boolean = yield call(ValidateOtp);

      if (frozenSagaValidPhone && frozenSagaValidOtp) {
        yield put(setOTPModalAction(false));

        screeningDone();
      }
    }
  }
}

function* MissingPhoneNoOtp(action: Action & { payload: { phoneNoOtp: string } }) {
  const phoneNoOtp = action.payload.phoneNoOtp;

  if (!phoneNoOtp) {
    yield put(setOTPModalAction(false));
    yield put(setOTPNoPhoneNumberModalAction(true));
  }
}

function* OTPVerify({ payload: otpCode }: AppAction<string>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(api.OTPVerify, yield getApiUrl(environment.urlConfig.user), otpCode),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);
  const verifyOtpResponseData: VerifyOtpResponse = resp.data;

  if (resp.ok) {
    yield put(verifyOTPCodeAction.success());
    yield put(getKahlActions.request({ infoMessageIds: 'ID0599' }));
  } else if (resp.status === 409) {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0596' }));
    yield put(verifyOTPCodeAction.failure(verifyOtpResponseData));
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0598' }));
  }
}

/* EXPORT */
export function* OTPSaga() {
  yield takeLeading(createActionType(OTPActionTypes.OTPSendSms, RequestActionTypes.REQUEST), OTPSendSms);

  yield takeLeading(createActionType(OTPActionTypes.VerifyOTPCode, RequestActionTypes.REQUEST), OTPVerify);

  yield takeLatest(createActionType(OTPActionTypes.RunOTPScreening), RunOTPScreening);

  yield takeLatest(createActionType(OTPActionTypes.OTPSendSms, RequestActionTypes.SUCCESS), MissingPhoneNoOtp);
}
